import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"
// The default "tom-select" entry is the complete build, which already
// registers the virtual_scroll plugin used for infinite scroll.
import TomSelect from "tom-select"

const PREVIEW_ATTR = "data-participant-preview"
const SPINNER =
  '<span class="inline-block size-4 animate-spin rounded-full border-2 border-border border-t-primary" aria-hidden="true"></span>'

// Connects to data-controller="pipeline--participant-picker"
//
// The booking form's External participants picker (CONEK-7690) — the Hotwire
// twin of v1's external-participants-field.tsx (contacts + typed emails).
// Internal participants keep the plain Ui::MultiSelectComponent. The
// controller owns a Tom Select over a <select> that carries no `name`: Tom
// Select is the search box + dropdown, the chosen values are shown as
// removable Ui::PillComponent pills below it (v1 participant-pills), and #sync
// writes the hidden params the form has always submitted:
// booking[contact_ids][] for a contact (a UUID) and booking[external_emails][]
// for a typed email (has "@").
//
// What it adds over the plain ui--select it replaces, all v1 behaviour:
//   * infinite scroll — the next page (skip/take) loads as the list is scrolled
//     to the bottom (Tom Select's virtual_scroll plugin), reset on a new query;
//   * v1 rows — avatar, job title, name, email (ContactSelectItem);
//   * a contact with no email is greyed out and cannot be picked;
//   * the eye icon on a contact row opens the shared contact preview modal
//     (ContactsController#preview, a Turbo Frame fragment) without picking it.
export default class extends Controller {
  static targets = [
    "select",
    "hidden",
    "pills",
    "pillTemplate",
    "avatarTemplate",
    "previewIconTemplate",
  ]

  static values = {
    searchUrl: String,
    pageSize: { type: Number, default: 10 },
    placeholder: String,
    noResults: String,
    emailPattern: String,
    addEmailLabel: String,
    previewLabel: String,
    previewUrl: String,
    previewFrame: { type: String, default: "contact_preview_modal" },
    contactField: { type: String, default: "booking[contact_ids][]" },
    emailField: { type: String, default: "booking[external_emails][]" },
  }

  connect() {
    this._onPreviewEscape = this._onPreviewEscape.bind(this)
    this._onDropdownPointer = this._onDropdownPointer.bind(this)
    this._dropPreviewFrame = this._dropPreviewFrame.bind(this)
    this.tomSelect = new TomSelect(this.selectTarget, this._config())

    // Clicks on a row's eye icon are caught before Tom Select's own dropdown
    // handler (capture phase), so opening the preview never picks the row.
    this.tomSelect.dropdown.addEventListener(
      "mousedown",
      this._onDropdownPointer,
      true,
    )
    this.tomSelect.dropdown.addEventListener(
      "click",
      this._onDropdownPointer,
      true,
    )

    // The search box keeps its text after a pick (clearAfterSelect: false). If
    // it is empty — e.g. a typed email was just added — show the unfiltered
    // first page again.
    this.tomSelect.on("item_add", (value) => {
      const ts = this.tomSelect
      // A typed email that was just added as an invite IS the search text, so
      // clear it; a picked contact keeps the search (v1 parity).
      if (String(value).trim() === ts.inputValue().trim()) {
        ts.setTextboxValue("")
      }
      if (ts.inputValue() === "") ts.trigger("type", "")
    })
    this.tomSelect.on("dropdown_open", (dropdown) => this._place(dropdown))

    window.addEventListener("keydown", this._onPreviewEscape, true)
    document.addEventListener("turbo:before-cache", this._dropPreviewFrame)

    // Edit mode: the server-rendered selection becomes hidden params + pills
    // before the first change.
    this.sync()
  }

  disconnect() {
    window.removeEventListener("keydown", this._onPreviewEscape, true)
    document.removeEventListener("turbo:before-cache", this._dropPreviewFrame)
    this.tomSelect?.destroy()
    this.tomSelect = null
    this._dropPreviewFrame()
  }

  // The preview frame is appended to <body> (outside the booking modal), so it
  // does not go away with the form, and a cached snapshot must not carry it
  // (CONEK-6282): drop the one this field created.
  _dropPreviewFrame() {
    this._previewObserver?.disconnect()
    this._previewObserver = null
    this._previewFrame?.remove()
    this._previewFrame = null
  }

  // Rebuild the hidden params and the pills from the <select>'s selection.
  // Bound to the select's `change` (Tom Select fires it on every add/remove).
  sync() {
    const options = Array.from(this.selectTarget.selectedOptions)
    this.hiddenTarget.replaceChildren(
      ...options.map((option) => this._hiddenInput(option.value)),
    )
    if (this.hasPillsTarget) {
      this.pillsTarget.replaceChildren(
        ...options.map((option) => this._pill(option)),
      )
    }
  }

  // `pill:remove` from a pill's ui--pill controller → drop that participant.
  removePill(event) {
    const value = event.detail?.pill?.dataset?.value
    if (value === undefined || !this.tomSelect) return
    this.tomSelect.removeItem(value)
  }

  // --- Tom Select ---

  _config() {
    const pageSize = this.pageSizeValue

    const config = {
      plugins: ["virtual_scroll"],
      // Read each server-rendered <option>'s `data-data` JSON (name, email)
      // into its Tom Select option, for the edit-mode pills.
      dataAttr: "data",
      valueField: "id",
      labelField: "fullName",
      searchField: [],
      // The endpoint is authoritative: never re-filter its rows client-side,
      // but only show the rows it returned for the CURRENT query. Every loaded
      // row is tagged with the query that fetched it (`_q`, see _load); without
      // this the unfiltered first page stayed listed under any search.
      score: (search) => (item) => (item._q === search ? 1 : 0),
      maxItems: null,
      maxOptions: null,
      hideSelected: true,
      // v1 parity: the search stays after a pick, so several matches of the
      // same query ("mariano") can be added in a row; the picked one drops out
      // of the list (hideSelected).
      clearAfterSelect: false,
      preload: "focus",
      placeholder: this.placeholderValue,
      firstUrl: (query) => ({ query, skip: 0 }),
      load: (query, callback) => this._load(query, callback, pageSize),
      render: {
        option: (data, escape) => this._contactOption(data, escape),
        // The selection lives in the pills below the box (v1), not as chips
        // inside it; Tom Select still needs its item nodes, so they are hidden.
        item: () => '<div class="hidden!"></div>',
        option_create: (data, escape) =>
          `<div class="create">${escape(this.addEmailLabelValue).replace("__EMAIL__", `<span>${escape(data.input.trim())}</span>`)}</div>`,
        no_results: () =>
          `<div class="no-results">${this._escape(this.noResultsValue)}</div>`,
        loading: () => `<div class="flex justify-center py-3">${SPINNER}</div>`,
        loading_more: () =>
          `<div class="flex justify-center py-3">${SPINNER}</div>`,
        no_more_results: () => "",
      },
    }

    // A typed, valid email becomes an invite (booking[external_emails][]),
    // unless a loaded contact already carries that address (v1 canAddEmail).
    const pattern = new RegExp(this.emailPatternValue || "^\\S+@\\S+\\.\\S+$")
    config.create = (input) => {
      const email = input.trim()
      return { id: email, fullName: email, email }
    }
    config.createFilter = (input) => {
      const email = input.trim()
      return pattern.test(email) && !this._emailIsKnown(email)
    }
    config.persist = false

    return config
  }

  async _load(query, callback, pageSize) {
    const ts = this.tomSelect
    if (!ts) return
    const page = ts.getUrl(query)
    try {
      const url = new URL(this.searchUrlValue, window.location.origin)
      // Both names: PackSupport::SearchParams endpoints read `query`, the
      // contacts search also accepts `q` (CONEK-4230 / CONEK-6570).
      url.searchParams.set("q", query)
      url.searchParams.set("query", query)
      url.searchParams.set("skip", String(page.skip))
      url.searchParams.set("take", String(pageSize))
      // Page snapshot: the first page of a search starts over; later pages
      // echo what that first page returned. Same contract as the React
      // selectors (use-selector-api.ts mergePageParams): own params win and
      // q/query/skip/take are never taken from the server. See
      // docs/technical/meilisearch.md "Page snapshot (page_params)".
      if (page.skip === 0) {
        this._pageParams = {}
      } else {
        this._mergePageParams(url.searchParams)
      }

      const response = await get(url.toString(), { responseKind: "json" })
      const data = response.ok ? await response.json : {}
      // A response for a query the user has already typed past must not
      // overwrite the current list: hand Tom Select back what it already has.
      if (this.tomSelect !== ts || query !== ts.lastValue) {
        callback(this._unselectedOptions(ts))
        return
      }

      if (page.skip === 0) {
        this._pageParams = this._scalarPageParams(data.page_params)
      }

      const rows = Array.isArray(data.results) ? data.results : []
      const total = Number(data.total ?? 0)
      if (page.skip + rows.length < total && rows.length > 0) {
        ts.setNextUrl(query, { query, skip: page.skip + rows.length })
      }
      const tagged = rows.map((row) => ({ ...row, _q: query }))
      callback(tagged.map((row) => this._withEmailGate(row)))
    } catch {
      callback()
    }
  }

  _scalarPageParams(pageParams) {
    const out = {}
    if (!pageParams || typeof pageParams !== "object") return out
    if (Array.isArray(pageParams)) return out
    Object.entries(pageParams).forEach(([key, value]) => {
      if (["string", "number", "boolean"].includes(typeof value)) {
        out[key] = String(value)
      }
    })
    return out
  }

  _mergePageParams(params) {
    Object.entries(this._pageParams ?? {}).forEach(([key, value]) => {
      if (["q", "query", "skip", "take"].includes(key)) return
      if (!params.has(key)) params.set(key, value)
    })
  }

  _unselectedOptions(ts) {
    return Object.values(ts.options).filter(
      (option) => !ts.items.includes(String(option.id)),
    )
  }

  // v1 `disabled: !hasEmail(contact)`: Tom Select renders a `disabled` option
  // without `data-selectable`, so it can be neither clicked nor key-picked.
  _withEmailGate(row) {
    return { ...row, disabled: !this._emailOf(row) }
  }

  _emailIsKnown(email) {
    const lower = email.toLowerCase()
    return Object.values(this.tomSelect?.options ?? {}).some((option) =>
      [option.email, ...(option.emails ?? [])].some(
        (value) => typeof value === "string" && value.toLowerCase() === lower,
      ),
    )
  }

  // Auto-flip like ui--select: open upward only when the list does not fit
  // below the control and there is more room above.
  _place(dropdown) {
    const rect = this.tomSelect.control.getBoundingClientRect()
    const below = window.innerHeight - rect.bottom
    const above = rect.top
    const up = dropdown.getBoundingClientRect().height > below && above > below
    dropdown.style.top = up ? "auto" : "100%"
    dropdown.style.bottom = up ? "100%" : "auto"
  }

  // --- Rows ---

  // v1 ContactSelectItem: avatar, optional job title, name, email, and an eye
  // icon (selectable rows only). A row with no email is greyed out.
  _contactOption(data, escape) {
    const name = this._contactName(data)
    const email = this._emailOf(data)
    const disabled = Boolean(data.disabled)
    const muted = disabled ? " opacity-50" : ""
    const jobTitle = data.jobTitle
      ? `<p class="text-foreground-medium truncate text-xs font-medium">${escape(data.jobTitle)}</p>`
      : ""
    const emailLine = email
      ? `<p class="text-foreground truncate text-sm font-normal">${escape(email)}</p>`
      : ""
    const eye =
      disabled || !this.previewUrlValue
        ? ""
        : `<button type="button" tabindex="-1" class="text-foreground flex w-6 shrink-0 justify-end" ${PREVIEW_ATTR}="${escape(String(data.id))}" aria-label="${escape(this.previewLabelValue.replace("__NAME__", name))}" data-testid="participant-preview">${this._previewIcon()}</button>`
    return `<div class="flex w-full items-center gap-2${disabled ? " cursor-not-allowed" : ""}" data-testid="participant-option"${disabled ? ' aria-disabled="true"' : ""}>
      <div class="shrink-0${muted}">${this._avatar(name, data.avatarUrl || data.pictureUrl)}</div>
      <div class="flex min-w-0 flex-1 flex-col justify-center${muted}">${jobTitle}<h3 class="text-foreground truncate text-base font-medium">${escape(name)}</h3>${emailLine}</div>
      ${eye}
    </div>`
  }

  _avatar(name, src) {
    if (!this.hasAvatarTemplateTarget) return ""
    const avatar =
      this.avatarTemplateTarget.content.firstElementChild.cloneNode(true)
    avatar.textContent = this._initials(name)
    if (src) {
      const img = document.createElement("img")
      img.src = src
      img.alt = name
      img.className = "absolute inset-0 h-full w-full object-contain"
      // ui--profile-picture drops a broken image so the initials show.
      img.setAttribute("data-ui--profile-picture-target", "image")
      img.setAttribute("data-action", "error->ui--profile-picture#onError")
      avatar.appendChild(img)
    }
    return avatar.outerHTML
  }

  _previewIcon() {
    return this.hasPreviewIconTemplateTarget
      ? this.previewIconTemplateTarget.innerHTML
      : ""
  }

  // Ui::ProfilePictureComponent's rule: first letter of the first two words,
  // else the first two characters.
  _initials(name) {
    const words = (name || "").trim().split(/\s+/).filter(Boolean)
    if (words.length === 0) return ""
    const raw =
      words.length >= 2 ? words[0][0] + words[1][0] : words[0].slice(0, 2)
    return raw.toUpperCase()
  }

  // --- Pills + params ---

  _pill(option) {
    const label = this._pillLabel(option.value)
    const pill =
      this.pillTemplateTarget.content.firstElementChild.cloneNode(true)
    // v1 ParticipantPill tint (participant-pills.tsx). Ui::PillComponent's
    // html_options sig takes no `class:` string, so it is applied here.
    pill.classList.add("bg-border!", "text-foreground!", "text-2xs!")
    pill.dataset.value = option.value
    const text = pill.querySelector("span")
    if (text) text.textContent = label
    const aria = pill.getAttribute("aria-label")
    if (aria) pill.setAttribute("aria-label", aria.replace("__TEXT__", label))
    return pill
  }

  // v1 pill labels: a contact → "Name (email)"; a typed email → the email.
  _pillLabel(value) {
    const data = this.tomSelect?.options?.[value] ?? {}
    const email = this._emailOf(data)
    const name = this._contactName(data)
    if (name && email && name !== email) return `${name} (${email})`
    return email || name || value
  }

  _hiddenInput(value) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.value = value
    input.name = value.includes("@")
      ? this.emailFieldValue
      : this.contactFieldValue
    return input
  }

  // --- Contact preview ---

  _onDropdownPointer(event) {
    const trigger = event.target.closest?.(`[${PREVIEW_ATTR}]`)
    if (!trigger) return
    event.preventDefault()
    event.stopPropagation()
    if (event.type === "click")
      this._openPreview(trigger.getAttribute(PREVIEW_ATTR))
  }

  // Load ContactsController#preview into the `contact_preview_modal` frame. The
  // frame lives on <body>, outside the booking modal, so the fixed overlay is
  // not clipped by the modal panel and paints above it.
  _openPreview(id) {
    if (!id || !this.previewUrlValue) return
    const src = this.previewUrlValue.replace("__ID__", encodeURIComponent(id))
    let frame = document.getElementById(this.previewFrameValue)
    if (!frame) {
      frame = document.createElement("turbo-frame")
      frame.id = this.previewFrameValue
      document.body.appendChild(frame)
      this._previewFrame = frame
    }
    if (
      frame.getAttribute("src") === src &&
      typeof frame.reload === "function"
    ) {
      frame.reload()
    } else {
      frame.setAttribute("src", src)
    }
    // Close the list while the preview is up, but reopen it on the same search
    // once the preview closes, so the user does not have to search again.
    const query = this.tomSelect?.lastValue ?? ""
    this.tomSelect?.close()
    this._reopenAfterPreview(frame, query)
  }

  // Watch the preview's <dialog> until it closes (ui--modal drops `open`, or it
  // is removed with the frame), then restore the list on `query`.
  _reopenAfterPreview(frame, query) {
    this._previewObserver?.disconnect()
    let sawOpen = false
    const observer = new MutationObserver(() => {
      const open = Boolean(frame.querySelector("dialog[open]"))
      if (open) {
        sawOpen = true
        return
      }
      if (!sawOpen) return
      observer.disconnect()
      this._previewObserver = null
      this._restoreSearch(query)
    })
    observer.observe(frame, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["open"],
    })
    this._previewObserver = observer
  }

  _restoreSearch(query) {
    const ts = this.tomSelect
    if (!ts || !this.element.isConnected) return
    ts.focus()
    if (!query) {
      ts.refreshOptions(true)
      return
    }
    ts.setTextboxValue(query)
    // virtual_scroll still points at the next page of this query; restart it
    // so the list comes back on its first page, as the user left it.
    if (typeof ts.clearPagination === "function") ts.clearPagination()
    // Closing the list dropped the unselected options, so fetch the search
    // again (same path as typing it; virtual_scroll restarts its paging).
    if (typeof ts.onSearchChange === "function") ts.onSearchChange(query)
    else ts.load(query)
    ts.refreshOptions(true)
  }

  // Every open Ui::ModalComponent closes on Escape (a document listener each),
  // so one Escape over the preview would also dismiss the booking modal behind
  // it. While the preview is open, take Escape first and close only it.
  _onPreviewEscape(event) {
    if (event.key !== "Escape") return
    const frame = document.getElementById(this.previewFrameValue)
    const dialog = frame?.querySelector("dialog[open]")
    if (!dialog) return
    event.preventDefault()
    event.stopImmediatePropagation()
    const modal = this.application.getControllerForElementAndIdentifier(
      dialog,
      "ui--modal",
    )
    if (modal) modal.close()
    else dialog.close()
  }

  // --- Helpers ---

  _contactName(data) {
    return (
      data.fullName ||
      [data.firstName, data.lastName].filter(Boolean).join(" ") ||
      this._emailOf(data) ||
      ""
    )
  }

  _emailOf(data) {
    return data.emails?.[0] || data.email || ""
  }

  _escape(value) {
    const div = document.createElement("div")
    div.textContent = value ?? ""
    return div.innerHTML
  }
}
